import { TableParamTypeEnum } from "@selector/shared/graphql";
import chalk from "chalk";
import fuzzysort from "fuzzysort";
import { Just, type Maybe, Nothing } from "purify-ts";
import { type FC, memo, type ReactElement, useCallback, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { match, P } from "ts-pattern";
import {
  Callout,
  CalloutIntent,
  ComboBox,
  DecimalField,
  Item,
  MenuOptionItem,
  MultiSelectMenu,
  Switch,
  TextField,
  VStack,
} from "widgets";
import { areArraysEqualIgnoreOrder, type GetArrayElementType } from "widgets/utils";

import {
  FileSelectionTypeEnum,
  type GenomeConstraints,
  type InputSampleValueInput,
  WorkflowParameterTypeEnum,
} from "@/graphql.ts";
import { FilesSelect } from "@/modules/files/components/FilesSelect.tsx";
import { CoordinatesWidget, CoordinatesWidgetCore } from "@/modules/workflow/components/parameters/CoordinatesWidget";
import { GenomeSelectionWidget } from "@/modules/workflow/components/parameters/GenomeSelectionWidget.tsx";
import { VariantsWidget, VariantsWidgetCore } from "@/modules/workflow/components/parameters/VariantsWidget";
import { SampleSelectionInput } from "@/modules/workflow/components/SampleSelectionInput.tsx";
import { WorkflowRegionField } from "@/modules/workflow/components/WorkflowRegionField.tsx";
import { useGetSampleTypeQuery } from "@/modules/workflow/graphql/workflow.gql.ts";
import { useWorkflowEditorStore } from "@/modules/workflow/stores/workflow-editor-store.ts";
import type {
  ActivityControlledValue,
  AllParametersTypes,
  BooleanParamType,
  FileParamType,
  NumberParamType,
  OrganismParamType,
  RegionParamType,
  SampleParamType,
  StringParamType,
  TableParamType,
  TrackerActivity,
  TrackerParamType,
  WorkflowActivityParameterFieldProps,
  WorkflowErrorConnector,
  WorkflowErrorParameter,
  WorkflowSampleAttributes,
} from "@/modules/workflow/utils/types";
import { ATTRIBUTE_TYPE, COLUMN_SET, FILE_BROWSE_TYPES } from "@/settings/settings.ts";

interface WorkflowParameterFieldProps<T> {
  parameter: WorkflowActivityParameterFieldProps;
  value: T;
  setValue: <U extends T>(value: Maybe<U>) => void;
  validateSingleParameter: () => void;
  error?: WorkflowErrorParameter | WorkflowErrorConnector;
  extraAction?: ReactElement;
  onInternalErrorChange?: (error: boolean) => void;
  // TODO Too specific?
  sampleTypeId?: number;
  selectedGenomesIds: number[];
  isPlaybookContext?: boolean;
  isPlaybookExecutionContext?: boolean;
}

const WorkflowParameterFieldRender = ({
  error,
  setValue,
  validateSingleParameter,
  extraAction,
  ...props
}: WorkflowParameterFieldProps<AllParametersTypes>) => {
  /**
   * For mandatory parameters, we must allow the removal of the value
   * because a P2 workflow can be used in a Playbook and the value will be set there.
   */

  const handleValueChange = useCallback(
    (value: number | null) => {
      setValue<NumberParamType>(value == null || !Number.isFinite(value) ? Nothing : Just([value]));
    },
    [setValue],
  );

  const handleViewSaved = useCallback(
    (viewId: number | null) => {
      setValue<TableParamType>(viewId != null ? Just(viewId) : Nothing);
      validateSingleParameter();
    },
    [setValue, validateSingleParameter],
  );

  const handleRegionValueChange = useCallback(
    (contigId: number | null, start: number, stop: number) => {
      let maybeValue: Maybe<RegionParamType> = Nothing;
      if (contigId != undefined) {
        maybeValue = Just({
          contigId,
          range: !Number.isNaN(start) && !Number.isNaN(stop) ? { start, stop } : undefined,
        });
      }
      setValue<RegionParamType>(maybeValue);
    },
    [setValue],
  );

  return match<WorkflowActivityParameterFieldProps, ReactElement>(props.parameter)
    .with(
      {
        __typename: "StringParam",
        multiSelect: P.union(P.optional(false), P.nullish),
        stringConstraints: P.union(P.nullish, { controlledValues: P.nullish }),
      },
      (parameter) => {
        const currentValue = props.value as StringParamType | null;

        return (
          <TextField
            label={parameter.label}
            name={parameter.id}
            value={currentValue?.[0] ?? ""}
            onChange={(value) => {
              setValue<StringParamType>(value === "" ? Nothing : Just([value]));
            }}
            onBlur={validateSingleParameter}
            isRequired={!parameter.optional}
            isDisabled={parameter.disabled}
            description={parameter.description}
            isInvalid={!!error}
            errorMessage={error?.message}
            extraAction={extraAction}
          />
        );
      },
    )
    .with(
      {
        __typename: "StringParam",
        stringConstraints: { controlledValues: P.array() },
      },
      (parameter) => {
        const currentValue = props.value as StringParamType | null;
        const selectedKeys = currentValue ? new Set(currentValue) : undefined;

        return (
          <MultiSelectMenu
            selectionMode={parameter.multiSelect === true ? "multiple" : "single"}
            label={parameter.label}
            isRequired={!parameter.optional}
            description={parameter.description}
            isDisabled={parameter.disabled}
            selectedKeys={selectedKeys}
            items={parameter.stringConstraints.controlledValues}
            onSelectionChange={(keys) => {
              let maybeValue: Maybe<StringParamType> = Nothing;

              if (keys === "all") {
                maybeValue = Just(parameter.stringConstraints.controlledValues.map((item) => item.value));
              } else if (keys.size > 0) {
                maybeValue = Just([...keys].map(String));
              }

              setValue<StringParamType>(maybeValue);
            }}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                validateSingleParameter();
              }
            }}
            isInvalid={!!error}
            errorMessage={error?.message}
            rendered={getSingleValueRenderedCb(currentValue, parameter.stringConstraints.controlledValues)}
            extraAction={extraAction}
            data-testid={parameter.id}
          >
            {(item) => (
              <Item key={item.value}>
                <MenuOptionItem title={item.label ?? item.value} description={item.description} />
              </Item>
            )}
          </MultiSelectMenu>
        );
      },
    )
    .with(
      {
        __typename: "NumberParam",
        multiSelect: P.union(P.optional(false), P.nullish),
        numberConstraints: P.when((i) => i?.controlledValues == null),
      },
      (parameter) => {
        const currentValue = props.value as NumberParamType | null;

        return (
          <DecimalField
            label={parameter.label}
            value={currentValue == null ? undefined : String(currentValue[0])}
            minValue={parameter.numberConstraints?.minValue ?? undefined}
            maxValue={parameter.numberConstraints?.maxValue ?? undefined}
            maxDecimals={parameter.numberConstraints?.maxDecimals ?? undefined}
            onValueChange={(value) => {
              if (value == null || value !== currentValue?.[0]) {
                handleValueChange(value);
              }
            }}
            onBlur={validateSingleParameter}
            isRequired={!parameter.optional}
            isDisabled={parameter.disabled}
            description={parameter.description}
            isInvalid={!!error}
            errorMessage={error?.message}
            extraAction={extraAction}
            name={parameter.id}
            onInternalErrorChange={props.onInternalErrorChange}
          />
        );
      },
    )
    .with(
      {
        __typename: "NumberParam",
        numberConstraints: { controlledValues: P.array() },
      },
      (parameter) => {
        const currentValue = props.value as NumberParamType | null;
        const selectedKeys = currentValue ? new Set(currentValue.map(String)) : undefined;

        return (
          <MultiSelectMenu
            selectionMode={parameter.multiSelect === true ? "multiple" : "single"}
            label={parameter.label}
            isRequired={!parameter.optional}
            description={parameter.description}
            isDisabled={parameter.disabled}
            selectedKeys={selectedKeys}
            items={parameter.numberConstraints.controlledValues}
            onSelectionChange={(keys) => {
              let maybeValue: Maybe<NumberParamType> = Nothing;

              if (keys === "all") {
                maybeValue = Just(parameter.numberConstraints.controlledValues.map((item) => item.value));
              } else if (keys.size > 0) {
                maybeValue = Just([...keys].map(Number));
              }

              setValue<NumberParamType>(maybeValue);
            }}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                validateSingleParameter();
              }
            }}
            isInvalid={!!error}
            errorMessage={error?.message}
            rendered={getSingleValueRenderedCb(currentValue, parameter.numberConstraints.controlledValues)}
            extraAction={extraAction}
            data-testid={parameter.id}
          >
            {(item) => (
              <Item key={String(item.value)}>
                <MenuOptionItem title={item.label ?? item.value} description={item.description} />
              </Item>
            )}
          </MultiSelectMenu>
        );
      },
    )
    .with({ __typename: "BooleanParam" }, (parameter) => {
      const currentValue = props.value as BooleanParamType | null;

      return (
        <Switch
          label={parameter.label}
          isSelected={currentValue ?? false}
          onChange={(value) => {
            setValue<BooleanParamType>(Just(value));
            // need to validate manually as the setValue explicitly does not validate as all other fields do it on blur
            validateSingleParameter();
          }}
          isRequired={!parameter.optional}
          isDisabled={parameter.disabled}
          description={parameter.description}
          isInvalid={!!error}
          errorMessage={error?.message}
          extraAction={extraAction}
          name={parameter.id}
        />
      );
    })
    .with({ __typename: "GenomeParam" }, (parameter) => {
      const currentValue = props.value as OrganismParamType | null;

      return (
        <GenomeSelectionWidget
          label={parameter.label}
          description={parameter.description}
          isRequired={!parameter.optional}
          isDisabled={parameter.disabled}
          errorMessage={error?.message}
          extraAction={extraAction}
          onSelectionChange={(keys) => {
            if (!areArraysEqualIgnoreOrder(keys, currentValue ?? [])) {
              setValue<OrganismParamType>(
                keys.length === 0 || !keys.every((k) => Number.isFinite(k)) ? Nothing : Just(keys),
              );
            }
            validateSingleParameter();
          }}
          selectedOrganisms={currentValue}
          multiSelection={parameter.multiSelect}
          genomeConstraints={parameter.genomeConstraints as GenomeConstraints}
        />
      );
    })
    .with({ __typename: "FileParam" }, (parameter) => {
      const currentValue = props.value as FileParamType | null;

      const selectableFileTypes: FILE_BROWSE_TYPES[] = [];

      if (parameter.fileConstraints?.fileSelectionType === FileSelectionTypeEnum.File)
        selectableFileTypes.push(FILE_BROWSE_TYPES.REGULAR_FILE);
      if (parameter.fileConstraints?.fileSelectionType === FileSelectionTypeEnum.Folder)
        selectableFileTypes.push(FILE_BROWSE_TYPES.DIRECTORY);

      return (
        <FilesSelect
          label={parameter.label}
          error={error ? new Error(error.message) : undefined}
          selection={currentValue ?? []}
          onSelect={(files) => {
            let maybeValue: Maybe<FileParamType> | null = Nothing;
            if (files.length > 0) {
              maybeValue = Just(files);
            }

            setValue<FileParamType>(maybeValue);
            validateSingleParameter();
          }}
          description={parameter.description}
          singleSelection={!parameter.multiSelect}
          fileDefinitionTypes={parameter.fileConstraints?.fileTypes ?? undefined}
          selectableFileTypes={selectableFileTypes.length > 0 ? selectableFileTypes : undefined}
          baseFilterHint={
            parameter.fileConstraints?.fileTypes
              ? `Only ${parameter.fileConstraints?.fileTypes.join(", ")} files can be selected.`
              : undefined
          }
          isDisabled={parameter.disabled}
          isRequired={!parameter.optional}
          extraAction={extraAction}
          data-testid={parameter.id}
        />
      );
    })
    .with({ __typename: "SampleParam" }, (parameter) => {
      const currentValue = props.value as InputSampleValueInput | null;

      return (
        <SampleSelectionInput
          {...parameter}
          onSelect={(selection: InputSampleValueInput) => {
            let maybeValue: Maybe<SampleParamType> = Nothing;
            if (selection != undefined) {
              maybeValue = Just(selection);
            }

            setValue<SampleParamType>(maybeValue);
            validateSingleParameter();
          }}
          isDisabled={parameter.disabled || !props.sampleTypeId}
          // Special behavior for sample file selection in Playbook execution context:
          // Do not allow to change the attribute (but allow picking samples) if the attribute was already set.
          isAttributeSelectDisabled={props.isPlaybookExecutionContext && !!currentValue?.attributeId}
          selection={currentValue}
          error={error ? new Error(error.message) : undefined}
          extraAction={extraAction}
          sampleTypeId={props.sampleTypeId}
          isRequired={!parameter.optional}
          data-testid={parameter.id}
        />
      );
    })
    .with({ __typename: "RegionParam" }, (parameter) => {
      const currentValue = props.value as RegionParamType | null;

      return (
        <WorkflowRegionField
          label={parameter.label}
          isPlaybookContext={props.isPlaybookContext}
          isRequired={!parameter.optional}
          isDisabled={parameter.disabled}
          description={parameter.description}
          isInvalid={!!error}
          errorMessage={error?.message}
          onChange={handleRegionValueChange}
          defaultValue={
            currentValue
              ? { contigId: currentValue.contigId, start: currentValue.range?.start, stop: currentValue.range?.stop }
              : undefined
          }
          extraAction={extraAction}
          // onInternalError
          onBlurWithin={() => {
            validateSingleParameter();
          }}
          selectedGenomesIds={props.selectedGenomesIds}
          data-testid={parameter.id}
          onInternalErrorChange={props.onInternalErrorChange}
        />
      );
    })
    .with({ __typename: "DateParam" }, (parameter) => {
      if (import.meta.env.DEV) {
        console.log(
          chalk.bgCyanBright(`Parameter ${parameter.__typename} '${parameter.label}' not supported`),
          parameter,
        );
      }

      return (
        <Callout intent={CalloutIntent.Warning} title={parameter.label ?? parameter.__typename}>
          <FormattedMessage id="parameter not supported" values={{ type: parameter.__typename }} />
        </Callout>
      );
    })
    .with({ __typename: "TrackerParam" }, (parameter) => {
      const currentValue = props.value as TrackerParamType | null;

      return (
        <TrackerParam
          currentValue={currentValue}
          validate={validateSingleParameter}
          {...parameter}
          error={error ? new Error(error.message) : undefined}
          setValue={setValue}
        />
      );
    })
    .with(
      {
        __typename: "StringParam",
        multiSelect: true,
        stringConstraints: P.union(P.nullish, { controlledValues: P.nullish }),
      },
      (parameter) => {
        // Can't be multi select and have no controlled values
        if (import.meta.env.DEV) {
          console.log(
            chalk.bgRedBright(`Parameter ${parameter.__typename} '${parameter.label}' is misconfigured`),
            parameter,
          );
        }

        return (
          <Callout intent={CalloutIntent.Danger} title={parameter.label ?? parameter.__typename}>
            <FormattedMessage id="parameter not supported" values={{ type: parameter.__typename }} />
          </Callout>
        );
      },
    )
    .with(
      {
        __typename: "NumberParam",
        multiSelect: P.boolean,
        numberConstraints: P._,
      },
      (parameter) => {
        // Can't be multi select and have no controlled values. Or any other weird configuration. Hard to spot them all properly.
        if (import.meta.env.DEV) {
          console.log(
            chalk.bgRedBright(`Parameter ${parameter.__typename} '${parameter.label}' is misconfigured`),
            parameter,
          );
        }

        return (
          <Callout intent={CalloutIntent.Danger} title={parameter.label ?? parameter.__typename}>
            <FormattedMessage id="parameter not supported" values={{ type: parameter.__typename }} />
          </Callout>
        );
      },
    )
    .with({ __typename: "TableParam", tableType: TableParamTypeEnum.CoordinateList }, (parameter) => {
      const currentValue = props.value as TableParamType | null;

      return props.isPlaybookContext ? (
        <CoordinatesWidgetCore
          {...parameter}
          error={error ? new Error(error.message) : undefined}
          currentValue={currentValue ?? undefined}
          onViewSaved={handleViewSaved}
          availableGenomesIds={props.selectedGenomesIds}
          extraAction={extraAction}
          workflowEventId={undefined}
          isPlaybookExecutionContext={props.isPlaybookExecutionContext}
          isDisabled={parameter.disabled}
        />
      ) : (
        <CoordinatesWidget
          {...parameter}
          error={error ? new Error(error.message) : undefined}
          currentValue={currentValue ?? undefined}
          onViewSaved={handleViewSaved}
          availableGenomesIds={props.selectedGenomesIds}
          extraAction={extraAction}
          isDisabled={parameter.disabled}
        />
      );
    })
    .with({ __typename: "TableParam", tableType: TableParamTypeEnum.Variant }, (parameter) => {
      const currentValue = props.value as TableParamType | null;

      return props.isPlaybookContext ? (
        <VariantsWidgetCore
          {...parameter}
          error={error ? new Error(error.message) : undefined}
          currentValue={currentValue ?? undefined}
          onViewSaved={handleViewSaved}
          availableGenomesIds={props.selectedGenomesIds}
          extraAction={extraAction}
          workflowEventId={undefined}
          isPlaybookExecutionContext={props.isPlaybookExecutionContext}
          isDisabled={parameter.disabled}
        />
      ) : (
        <VariantsWidget
          {...parameter}
          error={error ? new Error(error.message) : undefined}
          currentValue={currentValue ?? undefined}
          onViewSaved={handleViewSaved}
          availableGenomesIds={props.selectedGenomesIds}
          extraAction={extraAction}
          isDisabled={parameter.disabled}
        />
      );
    })
    .exhaustive();
};

const TrackerParam: FC<
  WorkflowActivityParameterFieldProps & {
    currentValue: TrackerParamType | null;
    error: Error | undefined;
    validate: () => void;
    setValue: <U extends AllParametersTypes>(value: Maybe<U>) => void;
  }
> = ({ currentValue, error, validate, setValue, ...parameter }) => {
  const { id } = parameter;
  const checkTrackerParameters = useWorkflowEditorStore((state) => state.checkTrackerParameters);
  const parametersRecord = useWorkflowEditorStore((state) => state.parametersRecord);
  const parameters = useMemo(() => Object.values(parametersRecord).flat(), [parametersRecord]);

  const sampleTypeId = useWorkflowEditorStore((state) => state.sampleTypeId);
  const {
    data,
    loading,
    error: getSampleTypeError,
  } = useGetSampleTypeQuery({
    variables: { sampleTypeId: sampleTypeId!, excludeAnalytical: false },
    skip: sampleTypeId == null,
  });

  const items = useMemo(() => {
    const attributeIsAlreadySelected = (attributeId: number): boolean =>
      parameters
        .filter((parameter) => parameter.typeName === "TrackerParam" && parameter.parameterId !== id)
        // .filter(
        //   (parameter) =>
        //     parameter.typeName === "TrackerParam" &&
        //     (parameter.parameterId !== id || (parameter.parameterId === id && parameter.nodeId !== nodeId)),
        // )
        .map((parameter) => parameter.values.extractNullable() as TrackerParamType | null)
        .includes(attributeId);

    return (
      data?.sample?.sampleType?.sampleAttributes.filter(
        (attribute) =>
          isAttributeSelectableForParamMapping(attribute, parameter as TrackerActivity) &&
          !attributeIsAlreadySelected(attribute.id),
      ) ?? []
    );
  }, [parameters, id, parameter, data]);

  const [filterTerm, setFilterTerm] = useState("");
  const filteredItems = useMemo(
    () =>
      fuzzysort.go(filterTerm.trim(), items, {
        all: true,
        keys: ["attributeName", "groupName"],
        scoreFn: (result) => {
          const attributeScore = result[0]?.score ?? 0;
          const groupScore = result[1]?.score ?? 0;
          return attributeScore * 3 + groupScore * 1;
        },
      }),
    [items, filterTerm],
  );

  return (
    <VStack>
      <ComboBox
        selectedKey={String(currentValue)}
        items={filteredItems}
        isRequired={!parameter.optional}
        label={parameter.label}
        description={parameter.description}
        isDisabled={parameter.disabled || loading || items.length === 0 || !sampleTypeId}
        isLoading={loading}
        placeholder={items.length === 0 && !loading ? "No attributes available" : "Select an attribute"}
        onSelectionChange={(key) => {
          if (key == null) {
            setValue(Nothing);
          } else {
            setValue(Just(Number(key)));
          }
        }}
        errorMessage={getSampleTypeError?.message ?? error?.message}
        isInvalid={!!getSampleTypeError || !!error}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            validate();
            checkTrackerParameters();
          }
        }}
        onInputChange={setFilterTerm}
      >
        {(item) => {
          const hasSearch = filterTerm.trim().length > 0;

          const titleHighlight =
            hasSearch && item[0]?.score != null && item[0].score > 0
              ? item[0].highlight((m, i) => (
                  <strong key={i} style={{ color: "var(--primary-main)" }}>
                    {m}
                  </strong>
                ))
              : item.obj.attributeName;
          const descriptionHighlight =
            hasSearch && item[1]?.score != null && item[1].score > 0
              ? item[1].highlight((m, i) => (
                  <strong key={i} style={{ color: "var(--primary-main)" }}>
                    {m}
                  </strong>
                ))
              : item.obj.groupName;

          return (
            <Item key={String(item.obj.id)} textValue={item.obj.attributeName}>
              <MenuOptionItem title={titleHighlight} description={descriptionHighlight} />
            </Item>
          );
        }}
      </ComboBox>

      {/* We removed the warning level in context of SEL10-1120 because the tracker params might also be mandatory now,
      meaning that these inputs do not only trigger warnings but also plain errors.
      Since we have no way to distinguish errors from warnings at this stage and it is too close to the release to do so,
      we simply remove the warning level callouts to the benefit of errors only, although this one specific error "data may be overwritten" is descrete and allows execution and publication.
      alreadySelected && (
        <Callout intent={CalloutIntent.Warning}>
          This attribute was selected in a different activity, so the data may be overwritten.
        </Callout>
      ) */}
    </VStack>
  );
};

const getSingleValueRenderedCb = (
  currentValue: (string | number)[] | undefined | null,
  items: ActivityControlledValue[],
) =>
  currentValue && currentValue.length === 1
    ? () => {
        const item = items.find((item) => String(item.value) === String(currentValue[0]));
        return item?.label ?? item?.value ?? undefined;
      }
    : undefined;

const isAttributeSelectableForParamMapping = (
  attribute: GetArrayElementType<WorkflowSampleAttributes>,
  parameter: TrackerActivity,
) => {
  const editableAttribute =
    // we allow now both analytical and annotation attributes as save targets (SEL10-1395);
    // this is in sync with the fact that both can be configured as inputs (although with some limited validation)
    //
    // we allow non-modifiable attributes since this is some kind of automation, not directly related to user modification (and consistent with the P1 mappings)
    // automatic attributes are excluded because they are not valid input for registration by the REST API via playbooks
    !attribute.automatic &&
    (attribute.columnSet.id === COLUMN_SET.COS_ANALYTICAL_ID.id ||
      attribute.columnSet.id === COLUMN_SET.COS_ANNOTATION_ID.id);

  // make sure param and attribute types are compatible
  const attributeTypeId = attribute.attributeType.id as ATTRIBUTE_TYPE;
  const matchingType = match<TrackerActivity, boolean>(parameter)
    .with(
      { type: WorkflowParameterTypeEnum.Text },
      () => attributeTypeId === ATTRIBUTE_TYPE.FREE_TEXT || attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT,
    )
    .with({ type: WorkflowParameterTypeEnum.Number }, () => attributeTypeId === ATTRIBUTE_TYPE.FREE_NUMERICAL)
    .with({ type: WorkflowParameterTypeEnum.Boolean }, () => attributeTypeId === ATTRIBUTE_TYPE.BOOLEAN)
    .with({ type: WorkflowParameterTypeEnum.Date }, () => attributeTypeId === ATTRIBUTE_TYPE.DATE)
    .with({ type: WorkflowParameterTypeEnum.File }, () => attributeTypeId === ATTRIBUTE_TYPE.FILE)
    .otherwise(() => {
      // Do nothing here. The components are in the Tracker Tab.
      if (import.meta.env.DEV) {
        console.log(
          chalk.bgCyanBright(
            `Parameter ${parameter.__typename} '${parameter.label}' of type '${parameter.type}' should not be rendered with Tracker attribute mappings.`,
          ),
          parameter,
        );
      }
      return false;
    });

  // multi params should only go into multi attributes, but single values might as well go into multi value attributes
  const matchingMultiValues = !parameter.multiSelect || parameter.multiSelect === attribute.multipleValues;

  return editableAttribute && matchingType && matchingMultiValues;
};

export const WorkflowParameterField = memo(WorkflowParameterFieldRender);
